import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, sgd, resolveImage, formatSG, preorderState } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";

const emptyLook = { number: "", name: "", slug: "", description: "", story: "", materials_fit: "", garment_measurements: "", hero_image: "", gallery: "",
  mood: "", tags: "", bundle_price: 0, is_new: true, is_featured: false,
  pieces: [{ id: crypto.randomUUID(), type: "top", name: "", price: 0, colors: "", sizes: "M,L", image: "", stock: 20 }] };

function SizeGuideEditor({ settingsDraft, setSettingsDraft, saveSettings, resetDraft }) {
  const parseRows = (raw) => {
    try { const r = JSON.parse(raw || "[]"); return Array.isArray(r) ? r : []; } catch { return []; }
  };
  const rows = parseRows(settingsDraft.size_guide_rows_json);
  const writeRows = (nextRows) => setSettingsDraft({ ...settingsDraft, size_guide_rows_json: JSON.stringify(nextRows) });
  const updateRow = (idx, patch) => writeRows(rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const removeRow = (idx) => writeRows(rows.filter((_, i) => i !== idx));
  const addRow = () => writeRows([...rows, { size: "", bust: "", waist: "", hip: "", bust_only: false }]);

  return (
    <div className="mt-10 border-t pt-10" style={{ borderColor: "#EBE5ED" }} data-testid="content-size-guide-block">
      <div className="flex items-center justify-between mb-4">
        <div className="font-serif-display text-[20px]">Size Guide</div>
        <div className="text-[11px] text-[#4A464A]/60">Edits apply to /size-guide and the pop-up drawer</div>
      </div>

      <label className="flex flex-col mb-5">
        <span className="text-[10px] uppercase tracking-editorial text-[#4A464A]/60 mb-1">Intro paragraph</span>
        <textarea rows={3} value={settingsDraft.size_guide_intro ?? ""} onChange={e => setSettingsDraft({ ...settingsDraft, size_guide_intro: e.target.value })} data-testid="content-size_guide_intro" className="border px-3 py-2 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }} />
      </label>

      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-editorial text-[#4A464A]/60 mb-2">Table rows</div>
        <div className="border" style={{ borderColor: "#EBE5ED" }}>
          <div className="grid grid-cols-12 text-[10px] uppercase tracking-editorial px-3 py-2 border-b gap-2" style={{ borderColor: "#EBE5ED", background: "#EBE5ED55" }}>
            <div className="col-span-2">Size</div>
            <div className="col-span-3">Bust</div>
            <div className="col-span-2">Waist</div>
            <div className="col-span-2">Hip</div>
            <div className="col-span-2">Full-width bust</div>
            <div className="col-span-1" />
          </div>
          {rows.map((r, idx) => (
            <div key={idx} className="grid grid-cols-12 px-3 py-2 gap-2 border-b last:border-b-0 items-center" style={{ borderColor: "#EBE5ED" }} data-testid={`admin-size-row-${idx}`}>
              <input value={r.size || ""} onChange={e => updateRow(idx, { size: e.target.value })} className="col-span-2 border px-2 py-1.5 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }} />
              <input value={r.bust || ""} onChange={e => updateRow(idx, { bust: e.target.value })} className="col-span-3 border px-2 py-1.5 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }} />
              <input value={r.waist || ""} onChange={e => updateRow(idx, { waist: e.target.value })} disabled={!!r.bust_only} className="col-span-2 border px-2 py-1.5 bg-transparent text-[13px] disabled:opacity-40" style={{ borderColor: "#EBE5ED" }} />
              <input value={r.hip || ""} onChange={e => updateRow(idx, { hip: e.target.value })} disabled={!!r.bust_only} className="col-span-2 border px-2 py-1.5 bg-transparent text-[13px] disabled:opacity-40" style={{ borderColor: "#EBE5ED" }} />
              <label className="col-span-2 flex items-center gap-2 text-[11px]">
                <input type="checkbox" checked={!!r.bust_only} onChange={e => updateRow(idx, { bust_only: e.target.checked })} data-testid={`admin-size-row-bustonly-${idx}`} />
                <span>Free size</span>
              </label>
              <button onClick={() => removeRow(idx)} data-testid={`admin-size-row-remove-${idx}`} className="col-span-1 text-[#4A464A]/60 hover:text-[#4A464A]"><X size={14} strokeWidth={1.4} /></button>
            </div>
          ))}
        </div>
        <button onClick={addRow} data-testid="admin-size-row-add" className="mt-3 text-[11px] uppercase tracking-editorial border px-3 py-1.5" style={{ borderColor: "#4A464A" }}>+ Add row</button>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {[
          { key: "size_guide_how_bust", label: "How to measure — Bust" },
          { key: "size_guide_how_waist", label: "How to measure — Waist" },
          { key: "size_guide_how_hip", label: "How to measure — Hip" },
        ].map(f => (
          <label key={f.key} className="flex flex-col">
            <span className="text-[10px] uppercase tracking-editorial text-[#4A464A]/60 mb-1">{f.label}</span>
            <textarea rows={3} value={settingsDraft[f.key] ?? ""} onChange={e => setSettingsDraft({ ...settingsDraft, [f.key]: e.target.value })} data-testid={`content-${f.key}`} className="border px-3 py-2 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }} />
          </label>
        ))}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {[
          { key: "size_guide_note_1", label: "Note 1" },
          { key: "size_guide_note_2", label: "Note 2" },
          { key: "size_guide_note_3", label: "Note 3" },
        ].map(f => (
          <label key={f.key} className="flex flex-col">
            <span className="text-[10px] uppercase tracking-editorial text-[#4A464A]/60 mb-1">{f.label}</span>
            <input value={settingsDraft[f.key] ?? ""} onChange={e => setSettingsDraft({ ...settingsDraft, [f.key]: e.target.value })} data-testid={`content-${f.key}`} className="border px-3 py-2 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }} />
          </label>
        ))}
      </div>

      <div className="mt-6 flex gap-3 justify-end">
        <button onClick={resetDraft} className="px-5 py-2.5 text-[11px] uppercase tracking-editorial border" style={{ borderColor: "#4A464A" }}>Reset</button>
        <button onClick={saveSettings} data-testid="admin-save-size-guide" className="px-5 py-2.5 text-[11px] uppercase tracking-editorial" style={{ background: "#4A464A", color: "#FDFBF7" }}>Save size guide</button>
      </div>
    </div>
  );
}


function ImageUpload({ value, onChange, token, testid }) {
  const ref = useRef();
  const [loading, setLoading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Only images"); return; }
    setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "X-Admin-Token": token } });
      // Store as relative path so it survives domain changes on redeploy
      onChange(data.url);
      toast.success("Uploaded");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="flex gap-2 items-start">
      <input value={value || ""} onChange={e => onChange(e.target.value)} placeholder="Paste image URL or upload →" className="flex-1 border px-3 py-2 bg-transparent text-[12px]" style={{ borderColor: "#EBE5ED" }} />
      <input ref={ref} type="file" accept="image/*" hidden onChange={e => upload(e.target.files?.[0])} data-testid={testid} />
      <button type="button" onClick={() => ref.current?.click()} disabled={loading} className="text-[11px] uppercase tracking-editorial border px-3 py-2 flex items-center gap-1" style={{ borderColor: "#4A464A" }}>
        <Upload size={12} strokeWidth={1.4} /> {loading ? "…" : "Upload"}
      </button>
      {value && <img src={resolveImage(value)} alt="preview" className="w-10 h-14 object-cover" />}
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("onelook_admin"));
  const [pw, setPw] = useState("");
  const [tab, setTab] = useState("looks");
  const [looks, setLooks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [promos, setPromos] = useState([]);
  const [social, setSocial] = useState([]);
  const [abandoned, setAbandoned] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [settings, setSettings] = useState({});
  const [settingsDraft, setSettingsDraft] = useState({});
  const [editing, setEditing] = useState(null);
  const [newPromo, setNewPromo] = useState({ code: "", kind: "percent", value: 10, min_spend: 0, max_uses: 0 });
  const [newSocial, setNewSocial] = useState({ image: "", look_slug: "", caption: "", order: 0 });

  const auth = { headers: { "X-Admin-Token": token } };

  const login = async () => {
    try { const { data } = await api.post("/admin/login", { password: pw });
      localStorage.setItem("onelook_admin", data.token); setToken(data.token); toast.success("Welcome");
    } catch { toast.error("Wrong password"); }
  };

  const load = async () => {
    if (!token) return;
    try {
      const [L, O, S, P, So, Ab, St] = await Promise.all([
        api.get("/looks"),
        api.get("/admin/orders", auth),
        api.get("/admin/newsletter", auth),
        api.get("/admin/promos", auth),
        api.get("/social"),
        api.get("/admin/abandoned", auth),
        api.get("/settings"),
      ]);
      setLooks(L.data); setOrders(O.data); setSubs(S.data); setPromos(P.data); setSocial(So.data); setAbandoned(Ab.data);
      setSettings(St.data); setSettingsDraft(St.data);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const toPayload = (f) => ({
    ...f,
    gallery: typeof f.gallery === "string" ? f.gallery.split(",").map(s => s.trim()).filter(Boolean) : f.gallery,
    mood: typeof f.mood === "string" ? f.mood.split(",").map(s => s.trim()).filter(Boolean) : f.mood,
    tags: typeof f.tags === "string" ? f.tags.split(",").map(s => s.trim()).filter(Boolean) : f.tags,
    bundle_price: Number(f.bundle_price),
    pieces: f.pieces.map(p => ({
      ...p, price: Number(p.price), stock: Number(p.stock || 0),
      colors: typeof p.colors === "string" ? p.colors.split(",").map(s => s.trim()).filter(Boolean) : p.colors,
      sizes: typeof p.sizes === "string" ? p.sizes.split(",").map(s => s.trim()).filter(Boolean) : p.sizes,
    })),
  });

  const saveLook = async () => {
    try {
      const payload = toPayload(editing);
      if (editing.id) await api.put(`/admin/looks/${editing.id}`, payload, auth);
      else await api.post("/admin/looks", payload, auth);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };

  const del = async (id) => { if (!window.confirm("Delete this look?")) return;
    await api.delete(`/admin/looks/${id}`, auth); toast.success("Deleted"); load();
  };

  const createPromo = async () => {
    try {
      await api.post("/admin/promos", { ...newPromo, code: newPromo.code.toUpperCase(), value: Number(newPromo.value), min_spend: Number(newPromo.min_spend), max_uses: Number(newPromo.max_uses) }, auth);
      toast.success("Promo created"); setNewPromo({ code: "", kind: "percent", value: 10, min_spend: 0, max_uses: 0 }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delPromo = async (id) => { await api.delete(`/admin/promos/${id}`, auth); load(); };

  const createSocial = async () => {
    try {
      await api.post("/admin/social", { ...newSocial, order: Number(newSocial.order) }, auth);
      toast.success("Post added"); setNewSocial({ image: "", look_slug: "", caption: "", order: 0 }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delSocial = async (id) => { await api.delete(`/admin/social/${id}`, auth); load(); };

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/abandoned/scan", {}, auth);
      toast.success(`Scan complete · nudged ${data.sent}, skipped ${data.skipped}`);
      load();
    } catch { toast.error("Scan failed"); }
    setScanning(false);
  };
  const delAbandoned = async (em) => { await api.delete(`/admin/abandoned/${encodeURIComponent(em)}`, auth); load(); };

  const saveSettings = async () => {
    try {
      const { data } = await api.put("/admin/settings", settingsDraft, auth);
      setSettings(data); setSettingsDraft(data);
      toast.success("Homepage copy updated ♡");
    } catch { toast.error("Save failed"); }
  };

  if (!token) return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="w-full max-w-sm text-center">
        <div className="font-serif-display text-[28px] tracking-tight">Admin</div>
        <input data-testid="admin-password" type="password" value={pw} onChange={e => setPw(e.target.value)} onKeyDown={e => e.key === "Enter" && login()} placeholder="Password" className="mt-6 w-full border px-4 py-3 bg-transparent text-[14px]" style={{ borderColor: "#EBE5ED" }} />
        <button data-testid="admin-login" onClick={login} className="mt-4 w-full py-3 text-[12px] uppercase tracking-editorial" style={{ background: "#4A464A", color: "#FDFBF7" }}>Sign in</button>
      </div>
    </div>
  );

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif-display text-[36px] tracking-tight">Admin</h1>
        <button onClick={() => { localStorage.removeItem("onelook_admin"); setToken(null); }} className="text-[11px] uppercase tracking-editorial">Sign out</button>
      </div>
      <div className="flex gap-6 text-[12px] uppercase tracking-editorial border-b mb-8 overflow-x-auto no-scrollbar" style={{ borderColor: "#EBE5ED" }}>
        {["looks", "orders", "newsletter", "promos", "social", "abandoned", "content"].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`pb-3 border-b-2 whitespace-nowrap ${tab === t ? "border-[#4A464A]" : "border-transparent"}`}>{t}</button>
        ))}
      </div>

      {tab === "looks" && (
        <div>
          <button onClick={() => setEditing({ ...emptyLook })} data-testid="admin-new-look" className="mb-6 px-5 py-2.5 text-[11px] uppercase tracking-editorial" style={{ background: "#4A464A", color: "#FDFBF7" }}>+ New Look</button>
          <div className="grid md:grid-cols-3 gap-6">
            {looks.map(l => (
              <div key={l.id} className="border p-4" style={{ borderColor: "#EBE5ED" }}>
                <img src={resolveImage(l.hero_image)} alt={l.name} className="w-full aspect-[3/4] object-cover mb-3" />
                <div className="text-[10px] uppercase tracking-editorial text-[#C3B1C5]">LOOK {l.number}</div>
                <div className="font-serif-display text-[18px]">{l.name}</div>
                <div className="text-[12px] mt-1">{sgd(l.bundle_price)}</div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => setEditing({ ...l, gallery: (l.gallery || []).join(", "), mood: (l.mood || []).join(", "), tags: (l.tags || []).join(", "), pieces: l.pieces.map(p => ({ ...p, colors: (p.colors || []).join(", "), sizes: (p.sizes || []).join(", ") })) })} className="text-[11px] uppercase tracking-editorial border px-3 py-1.5" style={{ borderColor: "#4A464A" }}>Edit</button>
                  <button onClick={() => del(l.id)} className="text-[11px] uppercase tracking-editorial border px-3 py-1.5" style={{ borderColor: "#4A464A" }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-3">
          {orders.length === 0 && <div className="text-[13px] text-[#4A464A]/60">No orders yet.</div>}
          {orders.map(o => (
            <div key={o.session_id} className="border p-4 text-[13px]" style={{ borderColor: "#EBE5ED" }}>
              <div className="flex items-center justify-between">
                <div className="font-mono text-[11px] text-[#4A464A]/60">{o.session_id.slice(0, 20)}…</div>
                <div className="flex items-center gap-2">
                  {o.was_preorder && (
                    <div className="text-[10px] uppercase tracking-editorial px-2 py-1" style={{ background: "#C3B1C5", color: "#FDFBF7" }} data-testid={`order-preorder-tag-${o.session_id.slice(0,10)}`}>Pre-order</div>
                  )}
                  {o.shipped_at && (
                    <div className="text-[10px] uppercase tracking-editorial px-2 py-1" style={{ background: "#AAB3A8", color: "#FDFBF7" }} data-testid={`order-shipped-tag-${o.session_id.slice(0,10)}`}>Shipped</div>
                  )}
                  <div className="text-[10px] uppercase tracking-editorial px-2 py-1" style={{ background: o.payment_status === "paid" ? "#AAB3A8" : "#EBE5ED", color: "#4A464A" }}>{o.payment_status}</div>
                </div>
              </div>
              <div className="mt-2">{o.email || "—"} · {sgd(o.amount)} · {o.items?.length || 0} items {o.promo_code ? `· promo ${o.promo_code}` : ""}</div>
              {o.was_preorder && o.preorder_close_date && (
                <div className="text-[11px] text-[#4A464A]/70 mt-1" data-testid={`order-preorder-dates-${o.session_id.slice(0,10)}`}>Closes {formatSG(o.preorder_close_date)}</div>
              )}
              <div className="text-[11px] text-[#4A464A]/60 mt-1">{o.created_at}</div>
              {o.payment_status === "paid" && o.email && (
                <div className="mt-3 flex items-center gap-3">
                  <button
                    data-testid={`order-send-shipping-${o.session_id.slice(0,10)}`}
                    onClick={async () => {
                      if (o.shipped_at && !window.confirm("Shipping email already sent — resend to " + o.email + "?")) return;
                      try {
                        await api.post(`/admin/orders/${o.session_id}/send-shipping-email`, {}, auth);
                        toast.success(`Shipping email sent to ${o.email}`);
                        load();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Could not send shipping email");
                      }
                    }}
                    className="px-3 py-1.5 text-[11px] uppercase tracking-editorial"
                    style={{ background: o.shipped_at ? "transparent" : "#4A464A", color: o.shipped_at ? "#4A464A" : "#FDFBF7", border: o.shipped_at ? "1px solid #4A464A" : "none" }}>
                    {o.shipped_at ? "Resend shipping email" : "Send shipping email"}
                  </button>
                  {o.shipped_at && (
                    <span className="text-[11px] text-[#4A464A]/60" data-testid={`order-shipped-at-${o.session_id.slice(0,10)}`}>Sent {new Date(o.shipped_at).toLocaleString("en-SG", { timeZone: "Asia/Singapore" })}</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "newsletter" && (
        <div className="space-y-2">
          {subs.length === 0 && <div className="text-[13px] text-[#4A464A]/60">No subscribers yet.</div>}
          {subs.map(s => (
            <div key={s.email} className="border p-3 text-[13px] flex justify-between" style={{ borderColor: "#EBE5ED" }}>
              <span>{s.email}</span><span className="text-[11px] text-[#4A464A]/60">{s.source} · {s.created_at?.slice(0, 10)}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "promos" && (
        <div>
          <div className="border p-4 mb-6 grid md:grid-cols-6 gap-3 items-end" style={{ borderColor: "#EBE5ED" }} data-testid="admin-new-promo">
            <label className="flex flex-col text-[11px] uppercase tracking-editorial">Code<input value={newPromo.code} onChange={e => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })} className="mt-1 border px-2 py-1.5 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }} /></label>
            <label className="flex flex-col text-[11px] uppercase tracking-editorial">Kind<select value={newPromo.kind} onChange={e => setNewPromo({ ...newPromo, kind: e.target.value })} className="mt-1 border px-2 py-1.5 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }}><option value="percent">% off</option><option value="amount">S$ off</option></select></label>
            <label className="flex flex-col text-[11px] uppercase tracking-editorial">Value<input type="number" value={newPromo.value} onChange={e => setNewPromo({ ...newPromo, value: e.target.value })} className="mt-1 border px-2 py-1.5 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }} /></label>
            <label className="flex flex-col text-[11px] uppercase tracking-editorial">Min spend<input type="number" value={newPromo.min_spend} onChange={e => setNewPromo({ ...newPromo, min_spend: e.target.value })} className="mt-1 border px-2 py-1.5 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }} /></label>
            <label className="flex flex-col text-[11px] uppercase tracking-editorial">Max uses (0=∞)<input type="number" value={newPromo.max_uses} onChange={e => setNewPromo({ ...newPromo, max_uses: e.target.value })} className="mt-1 border px-2 py-1.5 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }} /></label>
            <button onClick={createPromo} data-testid="admin-create-promo" className="py-2 text-[11px] uppercase tracking-editorial" style={{ background: "#4A464A", color: "#FDFBF7" }}>Create</button>
          </div>
          <div className="space-y-2">
            {promos.map(p => (
              <div key={p.id} className="border p-3 flex items-center justify-between text-[13px]" style={{ borderColor: "#EBE5ED" }}>
                <div>
                  <span className="font-serif-display text-[17px]">{p.code}</span>
                  <span className="ml-3 text-[11px] uppercase tracking-editorial text-[#C3B1C5]">{p.kind === "percent" ? `${p.value}% off` : `S$${p.value} off`}</span>
                  <span className="ml-3 text-[11px] text-[#4A464A]/60">min {sgd(p.min_spend)} · used {p.uses}{p.max_uses ? `/${p.max_uses}` : ""}</span>
                </div>
                <button onClick={() => delPromo(p.id)} className="text-[11px] uppercase tracking-editorial"><X size={14} /></button>
              </div>
            ))}
            {promos.length === 0 && <div className="text-[13px] text-[#4A464A]/60">No codes yet.</div>}
          </div>
        </div>
      )}

      {tab === "social" && (
        <div>
          {/* Social profile links */}
          <div className="border p-4 mb-6 space-y-3" style={{ borderColor: "#EBE5ED" }} data-testid="admin-social-profiles">
            <div className="font-serif-display text-[18px]">Your Profiles</div>
            <div className="text-[12px] text-[#4A464A]/70">Used on the homepage grid label, the Follow link below the grid, and the footer. Leave empty to hide.</div>
            <div className="grid md:grid-cols-3 gap-3">
              <label className="flex flex-col text-[11px] uppercase tracking-editorial">
                Instagram username
                <div className="mt-1 flex items-center border bg-transparent" style={{ borderColor: "#EBE5ED" }}>
                  <span className="px-2 text-[13px] text-[#4A464A]/50">@</span>
                  <input value={(settingsDraft.instagram_username || "").replace(/^@/, "")} onChange={e => setSettingsDraft({ ...settingsDraft, instagram_username: e.target.value.replace(/^@+/, "") })} data-testid="admin-ig-username" className="flex-1 py-1.5 bg-transparent text-[13px] outline-none" placeholder="onelook.sg" />
                </div>
              </label>
              <label className="flex flex-col text-[11px] uppercase tracking-editorial">
                TikTok username
                <div className="mt-1 flex items-center border bg-transparent" style={{ borderColor: "#EBE5ED" }}>
                  <span className="px-2 text-[13px] text-[#4A464A]/50">@</span>
                  <input value={(settingsDraft.tiktok_username || "").replace(/^@/, "")} onChange={e => setSettingsDraft({ ...settingsDraft, tiktok_username: e.target.value.replace(/^@+/, "") })} data-testid="admin-tiktok-username" className="flex-1 py-1.5 bg-transparent text-[13px] outline-none" placeholder="onelook.sg" />
                </div>
              </label>
              <label className="flex flex-col text-[11px] uppercase tracking-editorial">
                Xiaohongshu profile URL
                <input value={settingsDraft.xiaohongshu_url || ""} onChange={e => setSettingsDraft({ ...settingsDraft, xiaohongshu_url: e.target.value })} data-testid="admin-xhs-url" className="mt-1 border px-2 py-1.5 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }} placeholder="https://xiaohongshu.com/user/profile/..." />
              </label>
            </div>
            <div className="flex gap-3 justify-end pt-1">
              <button onClick={() => setSettingsDraft(settings)} className="px-4 py-2 text-[11px] uppercase tracking-editorial border" style={{ borderColor: "#4A464A" }}>Reset</button>
              <button onClick={saveSettings} data-testid="admin-save-social-profiles" className="px-4 py-2 text-[11px] uppercase tracking-editorial" style={{ background: "#4A464A", color: "#FDFBF7" }}>Save profiles</button>
            </div>
          </div>

          {/* Shoppable posts */}
          <div className="border p-4 mb-6 space-y-3" style={{ borderColor: "#EBE5ED" }} data-testid="admin-new-social">
            <div className="text-[11px] uppercase tracking-editorial text-[#4A464A]/70">Image</div>
            <ImageUpload value={newSocial.image} onChange={v => setNewSocial({ ...newSocial, image: v })} token={token} testid="admin-social-upload" />
            <div className="grid md:grid-cols-3 gap-3">
              <label className="flex flex-col text-[11px] uppercase tracking-editorial">Link to look (slug)
                <select value={newSocial.look_slug} onChange={e => setNewSocial({ ...newSocial, look_slug: e.target.value })} className="mt-1 border px-2 py-1.5 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }}>
                  <option value="">— none —</option>
                  {looks.map(l => <option key={l.id} value={l.slug}>LOOK {l.number} · {l.name}</option>)}
                </select>
              </label>
              <label className="flex flex-col text-[11px] uppercase tracking-editorial">Caption<input value={newSocial.caption} onChange={e => setNewSocial({ ...newSocial, caption: e.target.value })} className="mt-1 border px-2 py-1.5 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }} /></label>
              <label className="flex flex-col text-[11px] uppercase tracking-editorial">Order<input type="number" value={newSocial.order} onChange={e => setNewSocial({ ...newSocial, order: e.target.value })} className="mt-1 border px-2 py-1.5 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }} /></label>
            </div>
            <button onClick={createSocial} data-testid="admin-create-social" className="px-5 py-2 text-[11px] uppercase tracking-editorial" style={{ background: "#4A464A", color: "#FDFBF7" }}>Add post</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {social.map(s => (
              <div key={s.id} className="relative border" style={{ borderColor: "#EBE5ED" }}>
                <img src={resolveImage(s.image)} alt="" className="w-full aspect-square object-cover" />
                <div className="p-2 text-[11px]">
                  {s.look_slug && <div className="uppercase tracking-editorial text-[#C3B1C5]">→ {s.look_slug}</div>}
                  <div className="mt-1">{s.caption}</div>
                </div>
                <button onClick={() => delSocial(s.id)} className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "rgba(253,251,247,0.9)" }}><X size={13} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "abandoned" && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <div className="text-[13px] text-[#4A464A]/70">
              Bags with an email that were left untouched for 3+ hours receive a gentle nudge email (with a promo).
            </div>
            <button onClick={runScan} disabled={scanning} data-testid="admin-scan-abandoned" className="text-[11px] uppercase tracking-editorial px-4 py-2" style={{ background: "#4A464A", color: "#FDFBF7" }}>
              {scanning ? "Scanning…" : "Run scan now"}
            </button>
          </div>
          <div className="space-y-3">
            {abandoned.length === 0 && <div className="text-[13px] text-[#4A464A]/60">No abandoned bags yet.</div>}
            {abandoned.map(a => (
              <div key={a.email} className="border p-4 text-[13px]" style={{ borderColor: "#EBE5ED" }} data-testid={`abandoned-${a.email}`}>
                <div className="flex items-center justify-between">
                  <div className="font-serif-display text-[17px]">{a.email}</div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] uppercase tracking-editorial px-2 py-1" style={{ background: a.nudge_sent ? "#AAB3A8" : "#EBE5ED", color: "#4A464A" }}>
                      {a.purchased ? "purchased" : a.nudge_sent ? "nudged" : "pending"}
                    </span>
                    <button onClick={() => delAbandoned(a.email)} className="text-[11px] uppercase tracking-editorial"><X size={14} /></button>
                  </div>
                </div>
                <div className="mt-2 text-[12px]">{a.items?.length || 0} items · Subtotal {sgd(a.subtotal || 0)}</div>
                <div className="text-[11px] text-[#4A464A]/60 mt-1">Updated {a.updated_at?.slice(0, 19).replace("T", " ")}{a.nudge_sent_at ? ` · Nudged ${a.nudge_sent_at?.slice(0, 19).replace("T", " ")}` : ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "content" && (
        <div>
          <div className="mb-6">
            <div className="text-[13px] text-[#4A464A]/70">Edit any headline, kicker, or body text on the homepage. Changes appear live on save.</div>
          </div>

          {/* Pre-order mode */}
          {(() => {
            const po = preorderState(settingsDraft);
            return (
              <div className="border p-4 mb-6 space-y-3" style={{ borderColor: "#EBE5ED" }} data-testid="content-preorder-block">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-serif-display text-[18px]">Pre-order mode</div>
                    <div className="text-[12px] text-[#4A464A]/70 mt-1">When ON, the whole site shifts to pre-order copy with your close date + a 14-day ship window.</div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox"
                           data-testid="admin-preorder-toggle"
                           checked={String(settingsDraft.preorder_enabled || "").toLowerCase() === "true"}
                           onChange={e => setSettingsDraft({ ...settingsDraft, preorder_enabled: e.target.checked ? "true" : "false" })} />
                    <span className="text-[11px] uppercase tracking-editorial">{String(settingsDraft.preorder_enabled || "").toLowerCase() === "true" ? "On" : "Off"}</span>
                  </label>
                </div>
                <div className="grid md:grid-cols-3 gap-4 pt-2">
                  <label className="flex flex-col text-[11px] uppercase tracking-editorial">
                    Pre-order close date
                    <input type="date" value={settingsDraft.preorder_close_date || ""} onChange={e => setSettingsDraft({ ...settingsDraft, preorder_close_date: e.target.value })} data-testid="admin-preorder-close-date" className="mt-1 border px-2 py-1.5 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }} />
                  </label>
                  <div className="flex flex-col text-[11px] uppercase tracking-editorial">
                    Shipping starts
                    <div className="mt-1 border px-2 py-1.5 text-[13px]" style={{ borderColor: "#EBE5ED", background: "#F3ECE0" }} data-testid="admin-preorder-ship-date">
                      {po.shipDateFmt || "—"}
                    </div>
                    <span className="text-[10px] normal-case tracking-normal text-[#4A464A]/60 mt-1">Close date + 14 days (Singapore time)</span>
                  </div>
                  <div className="flex flex-col text-[11px] uppercase tracking-editorial">
                    Status
                    <div className="mt-1 border px-2 py-1.5 text-[13px]" style={{ borderColor: "#EBE5ED" }}>
                      {po.active ? "Active · accepting pre-orders" : po.closed ? "Closed · past date" : "Off"}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Hero visual controls (custom fields: image upload + look picker) */}
          <div className="border p-4 mb-6 space-y-4" style={{ borderColor: "#EBE5ED" }} data-testid="content-hero-visuals">
            <div className="font-serif-display text-[18px]">Hero visual</div>
            <div>
              <div className="text-[10px] uppercase tracking-editorial text-[#4A464A]/60 mb-1">Hero image (right side of homepage)</div>
              <ImageUpload value={settingsDraft.hero_image_url} onChange={v => setSettingsDraft({ ...settingsDraft, hero_image_url: v })} token={token} testid="admin-hero-image-upload" />
            </div>
            <label className="flex flex-col">
              <span className="text-[10px] uppercase tracking-editorial text-[#4A464A]/60 mb-1">Caption card — which look to feature</span>
              <select data-testid="content-hero-caption-look" value={settingsDraft.hero_caption_look_slug || ""} onChange={e => setSettingsDraft({ ...settingsDraft, hero_caption_look_slug: e.target.value })} className="border px-3 py-2 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }}>
                <option value="">— none —</option>
                {looks.map(l => <option key={l.id} value={l.slug}>LOOK {l.number} · {l.name} · {sgd(l.bundle_price)}</option>)}
              </select>
              <span className="text-[11px] text-[#4A464A]/60 mt-1">The small card over the hero image will pull this look's number, name and price automatically — and link to it when clicked.</span>
            </label>
          </div>

          {/* Brand logo for link-preview (OG) card */}
          <div className="border p-4 mb-6 space-y-3" style={{ borderColor: "#EBE5ED" }} data-testid="content-brand-logo">
            <div className="font-serif-display text-[18px]">Brand logo · link preview</div>
            <div className="text-[12px] text-[#4A464A]/70 leading-relaxed">
              Upload your ONE LOOK logo (PNG with transparent background works best — square or landscape, at least 600×400). It appears on the 1200×630 cream card social apps (Instagram, WhatsApp, Telegram, Facebook) show when your link is pasted. Leave blank to keep the current "ONE LOOK" wordmark card.
            </div>
            <ImageUpload value={settingsDraft.brand_logo_url} onChange={v => setSettingsDraft({ ...settingsDraft, brand_logo_url: v })} token={token} testid="admin-brand-logo-upload" />
            {settingsDraft.brand_logo_url && (
              <a href="/api/og-image.jpg" target="_blank" rel="noreferrer" data-testid="admin-og-preview-link" className="inline-block text-[11px] uppercase tracking-editorial border-b" style={{ borderColor: "#4A464A" }}>Preview generated card →</a>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {[
              { key: "announcement_text", label: "Top announcement bar", type: "input" },
              { key: "hero_kicker", label: "Hero kicker (small caps above headline)", type: "input" },
              { key: "hero_headline_1", label: "Hero headline — first line", type: "input" },
              { key: "hero_headline_2", label: "Hero headline — second line (lavender)", type: "input" },
              { key: "hero_body", label: "Hero body text", type: "textarea" },
              { key: "shop_the_look_title", label: "Section: Shop The Look — title", type: "input" },
              { key: "shop_the_look_subtitle", label: "Section: Shop The Look — subtitle", type: "input" },
              { key: "todays_look_title", label: "Section: Today's One Look — title", type: "input" },
              { key: "new_looks_title", label: "Section: New Looks — title", type: "input" },
              { key: "moods_title", label: "Section: Your Week — title", type: "input" },
              { key: "brand_story_title", label: "Section: Brand Story — title", type: "input" },
              { key: "brand_story_body", label: "Section: Brand Story — body", type: "textarea" },
              { key: "social_title", label: "Section: Instagram / Social — title", type: "input" },
              { key: "newsletter_title", label: "Section: Newsletter — title", type: "input" },
              { key: "newsletter_body", label: "Section: Newsletter — body", type: "textarea" },
              { key: "low_stock_threshold", label: "Low-stock whisper threshold (number — show \"Only X left ♡\" when stock ≤ this)", type: "input" },
            ].map(f => (
              <label key={f.key} className={`flex flex-col ${f.type === "textarea" ? "md:col-span-2" : ""}`}>
                <span className="text-[10px] uppercase tracking-editorial text-[#4A464A]/60 mb-1">{f.label}</span>
                {f.type === "textarea" ? (
                  <textarea rows={4} value={settingsDraft[f.key] ?? ""} onChange={e => setSettingsDraft({ ...settingsDraft, [f.key]: e.target.value })} data-testid={`content-${f.key}`} className="border px-3 py-2 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }} />
                ) : (
                  <input value={settingsDraft[f.key] ?? ""} onChange={e => setSettingsDraft({ ...settingsDraft, [f.key]: e.target.value })} data-testid={`content-${f.key}`} className="border px-3 py-2 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }} />
                )}
              </label>
            ))}
          </div>
          <div className="mt-8 flex gap-3 justify-end">
            <button onClick={() => setSettingsDraft(settings)} className="px-5 py-2.5 text-[11px] uppercase tracking-editorial border" style={{ borderColor: "#4A464A" }}>Reset</button>
            <button onClick={saveSettings} data-testid="admin-save-content" className="px-5 py-2.5 text-[11px] uppercase tracking-editorial" style={{ background: "#4A464A", color: "#FDFBF7" }}>Save changes</button>
          </div>

          {/* Size Guide editor */}
          <SizeGuideEditor settingsDraft={settingsDraft} setSettingsDraft={setSettingsDraft} saveSettings={saveSettings} resetDraft={() => setSettingsDraft(settings)} />
        </div>
      )}

      {editing && createPortal(
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-white max-w-2xl w-full my-8 p-6 relative" onClick={e => e.stopPropagation()}>
            <div className="font-serif-display text-[24px] mb-4">{editing.id ? "Edit" : "New"} Look</div>
            <div className="grid grid-cols-2 gap-3 text-[13px]">
              {["number", "name", "slug", "description", "story", "mood", "tags", "bundle_price"].map(k => (
                <label key={k} className="flex flex-col col-span-2">
                  <span className="text-[10px] uppercase tracking-editorial text-[#4A464A]/60 mb-1">{k}</span>
                  <input value={editing[k] || ""} onChange={e => setEditing({ ...editing, [k]: e.target.value })} className="border px-3 py-2 bg-transparent" style={{ borderColor: "#EBE5ED" }} />
                </label>
              ))}
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-editorial text-[#4A464A]/60 mb-1">hero_image</div>
                <ImageUpload value={editing.hero_image} onChange={v => setEditing({ ...editing, hero_image: v })} token={token} testid="admin-hero-upload" />
              </div>
              <label className="flex flex-col col-span-2">
                <span className="text-[10px] uppercase tracking-editorial text-[#4A464A]/60 mb-1">gallery (comma URLs)</span>
                <input value={editing.gallery || ""} onChange={e => setEditing({ ...editing, gallery: e.target.value })} className="border px-3 py-2 bg-transparent" style={{ borderColor: "#EBE5ED" }} />
              </label>
              <label className="flex flex-col col-span-2">
                <span className="text-[10px] uppercase tracking-editorial text-[#4A464A]/60 mb-1">materials_fit (shown in product accordion — leave empty to hide section)</span>
                <textarea rows={3} value={editing.materials_fit || ""} onChange={e => setEditing({ ...editing, materials_fit: e.target.value })} data-testid="admin-materials-fit" className="border px-3 py-2 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }} />
              </label>
              <label className="flex flex-col col-span-2">
                <span className="text-[10px] uppercase tracking-editorial text-[#4A464A]/60 mb-1">garment_measurements (optional — e.g. "Top length 58cm · Bust 94cm · Sleeve 42cm". Shown as a Measurements section on the product page and inside the size-guide drawer for this look)</span>
                <textarea rows={3} value={editing.garment_measurements || ""} onChange={e => setEditing({ ...editing, garment_measurements: e.target.value })} data-testid="admin-garment-measurements" className="border px-3 py-2 bg-transparent text-[13px]" style={{ borderColor: "#EBE5ED" }} />
              </label>
              <label className="flex items-center gap-2 col-span-2"><input type="checkbox" checked={editing.is_featured} onChange={e => setEditing({ ...editing, is_featured: e.target.checked })} /> Featured</label>
            </div>
            <div className="mt-6 font-serif-display text-[18px]">Pieces</div>
            {editing.pieces.map((p, idx) => (
              <div key={p.id} className="mt-3 border p-3 grid grid-cols-2 gap-2 text-[12px]" style={{ borderColor: "#EBE5ED" }}>
                {["type", "name", "price", "colors", "sizes", "stock"].map(k => (
                  <label key={k} className="flex flex-col"><span className="text-[10px] uppercase tracking-editorial text-[#4A464A]/60">{k}</span>
                    <input value={p[k] ?? ""} onChange={e => { const pcs = [...editing.pieces]; pcs[idx] = { ...pcs[idx], [k]: e.target.value }; setEditing({ ...editing, pieces: pcs }); }} className="border px-2 py-1 bg-transparent" style={{ borderColor: "#EBE5ED" }} /></label>
                ))}
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-editorial text-[#4A464A]/60">image</div>
                  <ImageUpload value={p.image} onChange={v => { const pcs = [...editing.pieces]; pcs[idx] = { ...pcs[idx], image: v }; setEditing({ ...editing, pieces: pcs }); }} token={token} testid={`admin-piece-upload-${idx}`} />
                </div>
              </div>
            ))}
            <button onClick={() => setEditing({ ...editing, pieces: [...editing.pieces, { id: crypto.randomUUID(), type: "top", name: "", price: 0, colors: "", sizes: "M,L", image: "", stock: 20 }] })} className="mt-3 text-[11px] uppercase tracking-editorial border px-3 py-1.5" style={{ borderColor: "#4A464A" }}>+ Add piece</button>
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 text-[11px] uppercase tracking-editorial border" style={{ borderColor: "#4A464A" }}>Cancel</button>
              <button onClick={saveLook} data-testid="admin-save-look" className="px-5 py-2.5 text-[11px] uppercase tracking-editorial" style={{ background: "#4A464A", color: "#FDFBF7" }}>Save</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
